/**
 * MarkdownTextarea Component
 * Reusable textarea with markdown preview
 * - Click to edit / blur to preview
 * - Ctrl+Enter to submit
 * - Auto-grow with content (up to maxHeight)
 * - Scroll when content exceeds maxHeight
 * - Markdown rendering in preview mode with scroll
 * - Magic wand button for AI prompt improvement
 */

import React, { useState, useRef, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import SendIcon from '@mui/icons-material/Send';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { PromptImprovementModal } from './PromptImprovementModal';
import { TextEditorModal } from './TextEditorModal';
import { CreditsIcon } from './CreditsIcon';
import promptImprovementService from '../../services/PromptImprovementService';
import irisService from '../../services/IrisService';
import { useTutorial } from '../../contexts/TutorialContext';

/**
 * Memoized Markdown Preview Component
 * Prevents re-parsing markdown on every parent render
 * Only re-parses when the content actually changes
 */
const MemoizedMarkdown = memo(({ content }: { content: string }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
    {content}
  </ReactMarkdown>
));
MemoizedMarkdown.displayName = 'MemoizedMarkdown';

export interface PromptImprovementContext {
  /** Type of content being generated */
  contentType?: 'image' | 'video' | 'text';
  /** The selected AI model id (e.g. 'gemini-nano-banana', 'flux-2-max') */
  selectedModel?: string;
  /** Any upstream context (text from connected nodes) */
  upstreamContext?: string;
  /** Image context description if available */
  imageContext?: string;
  /** Actual image URLs from connected nodes for visual analysis */
  imageUrls?: string[];
  /** Current node parameter values (duration, aspectRatio, resolution, quality, etc.) */
  nodeParameters?: Record<string, any>;
}

export interface MarkdownTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value?: string) => void;
  placeholder?: string;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
  minHeight?: string;
  maxHeight?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  showSubmitButton?: boolean;
  readOnly?: boolean;
  /** Credit cost to display on send button (optional) */
  creditCost?: number;
  /** When true, a BYOK key covers this node's provider — show 🔑, zero credits */
  usesOwnKey?: boolean;
  /** Enable magic wand button for prompt improvement */
  showMagicWand?: boolean;
  /** Context for prompt improvement (node type, connected inputs, etc.) */
  promptImprovementContext?: PromptImprovementContext;
  /** Whether there's a connected text input with content (enables send button even if textarea is empty) */
  hasConnectedTextInput?: boolean;
  /** Text content from connected nodes to show as placeholder when textarea is empty */
  connectedTextContent?: string;
  /** Extra buttons to render next to the magic wand (e.g., settings toggle) */
  extraButtons?: React.ReactNode;
  /** Whether settings panel is open (affects border-radius) */
  isSettingsOpen?: boolean;
  /** Function to condense text (for modal) */
  onCondenseRequest?: (text: string) => Promise<string>;
  /** Node name for modal title (e.g. "Edit Gemini Prompt") */
  nodeTitle?: string;
  /** Whether this node can generate (enables Accept & Generate button) */
  canGenerate?: boolean;
  /** Callback for Accept & Generate action, receives the accepted value */
  onAcceptAndGenerate?: (value: string) => void;
  /** Media output type for modal border color styling */
  mediaOutputType?: 'text' | 'image' | 'video';
  /** Callback when text editor modal opens (receives nodeId) */
  onTextEditorOpen?: (nodeId: string) => void;
  /** Node ID for identifying which node's modal opened */
  nodeId?: string;
  /** Inline settings fields for the editor modal toolbar */
  settingsFields?: import('./NodeSettingsFooter').SettingsFieldConfig[];
  /** Current settings values */
  settingsValues?: Record<string, any>;
  /** Callback when a setting changes in the modal toolbar */
  onSettingsChange?: (fieldId: string, value: any) => void;
}

const MarkdownTextareaComponent: React.FC<MarkdownTextareaProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Enter text...',
  variant = 'neutral',
  minHeight = '80px',
  maxHeight = '400px',
  // @ts-expect-error TS6133 - Kept for future use
  autoFocus = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  disabled = false,
  showSubmitButton = true,
  readOnly = false,
  creditCost,
  usesOwnKey = false,
  showMagicWand = false,
  promptImprovementContext,
  hasConnectedTextInput = false,
  connectedTextContent = '',
  extraButtons,
  isSettingsOpen = false,
  onCondenseRequest,
  nodeTitle = 'Edit Prompt',
  canGenerate = false,
  onAcceptAndGenerate,
  mediaOutputType,
  onTextEditorOpen,
  nodeId,
  settingsFields,
  settingsValues,
  onSettingsChange,
}) => {
  const normalizedMinHeight = minHeight;
  const normalizedMaxHeight = maxHeight;
  
  // @ts-expect-error TS6133 - Kept for future inline editing
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEditing, setIsEditing] = useState(false);
  // @ts-expect-error TS6133 - Kept for future inline editing
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const previewMouseDownRef = useRef<{ x: number; y: number } | null>(null);
  const PREVIEW_DRAG_THRESHOLD_PX = 6;
  
  // Text Editor Modal state
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  
  // Magic wand modal state (preserved for future use)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [improvedPrompt, setImprovedPrompt] = useState('');
  const [improvementError, setImprovementError] = useState<string | undefined>(undefined);

  // Note: Inline editing disabled - TextEditorModal handles all editing now
  // Preserved useEffect hooks for potential rollback to inline editing
  
  // Auto-focus when entering edit mode (disabled)
  // useEffect(() => {
  //   if (isEditing && textareaRef.current) {
  //     textareaRef.current.focus();
  //     textareaRef.current.setSelectionRange(
  //       textareaRef.current.value.length,
  //       textareaRef.current.value.length
  //     );
  //   }
  // }, [isEditing]);

  // Auto-focus on mount if specified (disabled)
  // useEffect(() => {
  //   if (autoFocus && textareaRef.current) {
  //     setIsEditing(true);
  //   }
  // }, [autoFocus]);

  // Handle Ctrl+Enter to submit (disabled)
  // const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
  //   if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
  //     e.preventDefault();
  //     if (onSubmit && !disabled) {
  //       onSubmit();
  //       setIsEditing(false);
  //     }
  //   }
  // }, [onSubmit, disabled]);

  // Handle blur to exit edit mode (disabled)
  // const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
  //   const relatedTarget = e.relatedTarget as HTMLElement;
  //   if (relatedTarget && (relatedTarget.classList.contains('send-button') || relatedTarget.classList.contains('magic-wand-button'))) {
  //     return;
  //   }
  //   if (!readOnly) {
  //     setIsEditing(false);
  //   }
  // }, [readOnly]);

  const { completeAction: tutorialCompleteAction } = useTutorial();

  // Open text editor modal from preview
  const openTextEditor = useCallback(() => {
    if (!readOnly && !disabled) {
      if (previewRef.current) {
        setOriginRect(previewRef.current.getBoundingClientRect());
      }
      setIsTextEditorOpen(true);
      tutorialCompleteAction('click', { target: '.markdown-textarea-container' });
      // Notify parent that text editor is opening (closes settings panel for this node)
      if (onTextEditorOpen && nodeId) {
        onTextEditorOpen(nodeId);
      }
    }
  }, [readOnly, disabled, onTextEditorOpen, nodeId, tutorialCompleteAction]);

  // Track mousedown position so onClick can distinguish a click from a drag
  const handlePreviewMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0 || readOnly || disabled) return;
    previewMouseDownRef.current = { x: e.clientX, y: e.clientY };
  }, [readOnly, disabled]);

  // onClick fires reliably even when the mouse drifts slightly off the element,
  // unlike onMouseUp. We compare against the recorded mousedown position to
  // reject drags (which React Flow handles) and only open the editor on real clicks.
  const handlePreviewClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly || disabled) return;
    const down = previewMouseDownRef.current;
    previewMouseDownRef.current = null;
    if (down) {
      const dx = Math.abs(e.clientX - down.x);
      const dy = Math.abs(e.clientY - down.y);
      if (dx > PREVIEW_DRAG_THRESHOLD_PX || dy > PREVIEW_DRAG_THRESHOLD_PX) return;
    }
    openTextEditor();
  }, [readOnly, disabled, openTextEditor]);

  // Handle TextEditorModal accept
  const handleModalAccept = useCallback(() => {
    setIsTextEditorOpen(false);
  }, []);

  // Handle TextEditorModal cancel
  const handleModalCancel = useCallback(() => {
    setIsTextEditorOpen(false);
  }, []);

  // Handle wheel events to prevent React Flow zoom when hovering preview content
  const handleWheelCapture = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight > target.clientHeight) {
      e.stopPropagation();
    }
  }, []);

  // Handle wheel events to prevent zoom on scrollable content
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isScrollable = target.scrollHeight > target.clientHeight;
    
    if (isScrollable) {
      // Always stop propagation to prevent React Flow zoom
      e.stopPropagation();
      
      // Only prevent default scroll if we're actually going to scroll
      // (not at top trying to scroll up, not at bottom trying to scroll down)
      const scrollTop = target.scrollTop;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;
      const canScrollDown = scrollTop < target.scrollHeight - target.clientHeight;
      const canScrollUp = scrollTop > 0;
      
      if ((isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)) {
        e.preventDefault();
      }
    }
  }, []);

  // Handle submit button click
  const handleSubmitClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSubmit && !disabled) {
      onSubmit(value);
    }
  }, [onSubmit, disabled, value]);

  // Handle magic wand button click
  const handleMagicWandClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!value.trim() || disabled) return;
    
    setIsModalOpen(true);
    setIsImproving(true);
    setImprovementError(undefined);
    setImprovedPrompt('');
    
    try {
      const result = await promptImprovementService.improvePrompt({
        originalPrompt: value,
        contentType: promptImprovementContext?.contentType || 'text',
        selectedModel: promptImprovementContext?.selectedModel,
        upstreamContext: promptImprovementContext?.upstreamContext,
        imageContext: promptImprovementContext?.imageContext,
        imageUrls: promptImprovementContext?.imageUrls,
        nodeParameters: promptImprovementContext?.nodeParameters,
      });
      
      if (result.success) {
        setImprovedPrompt(result.improvedPrompt);
      } else {
        setImprovementError(result.error || 'Failed to improve prompt');
      }
    } catch (error) {
      setImprovementError(error instanceof Error ? error.message : 'Failed to improve prompt');
    } finally {
      setIsImproving(false);
    }
  }, [value, disabled, promptImprovementContext]);

  // Handle using the improved prompt
  const handleUseImprovedPrompt = useCallback(() => {
    if (improvedPrompt) {
      onChange(improvedPrompt);
      setIsModalOpen(false);
      setImprovedPrompt('');
    }
  }, [improvedPrompt, onChange]);

  // Handle canceling the modal
  const handleCancelModal = useCallback(() => {
    setIsModalOpen(false);
    setImprovedPrompt('');
    setImprovementError(undefined);
  }, []);

  const containerClasses = [
    'markdown-textarea-container',
    variant,
    isSettingsOpen ? 'settings-open' : ''
  ].filter(Boolean).join(' ');

  // @ts-expect-error TS6133 - Kept for future inline editing
  const editClasses = [
    'markdown-textarea-edit',
    'nodrag', // Prevent React Flow drag
    variant
  ].filter(Boolean).join(' '); // eslint-disable-line @typescript-eslint/no-unused-vars

  const previewClasses = [
    'markdown-textarea-preview',
    'nowheel', // Prevent React Flow zoom while hovering this preview
    variant
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={containerClasses}
      style={{
        ['--min-height' as any]: normalizedMinHeight,
        ['--max-height' as any]: normalizedMaxHeight
      }}
    >
      {/* Always show preview - inline editing is disabled */}
      <div className="markdown-textarea-edit-wrapper">
        <div
          ref={previewRef}
          onMouseDown={handlePreviewMouseDown}
          onClick={handlePreviewClick}
          onWheelCapture={handleWheelCapture}
          onWheel={handleWheel}
          className={previewClasses}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openTextEditor();
            }
          }}
        >
          {value ? (
            <MemoizedMarkdown content={value} />
          ) : connectedTextContent ? (
            <span className="helper-text helper-text-italic" style={{ opacity: 0.5 }}>
              {connectedTextContent}
            </span>
          ) : (
            <span className="helper-text helper-text-italic">
              <MemoizedMarkdown content={placeholder} />
            </span>
          )}
        </div>
        {showMagicWand && value && (
          <button
            type="button"
            aria-label="Improve prompt with AI"
            onClick={handleMagicWandClick}
            onMouseDown={(e) => e.stopPropagation()}
            className={`magic-wand-button nodrag ${isSettingsOpen ? 'settings-shifted' : ''}`}
            disabled={disabled || !value.trim()}
            title="Improve prompt with AI"
          >
            <AutoFixHighIcon fontSize="small" />
          </button>
        )}
        {extraButtons}
        {showSubmitButton && onSubmit && (
          <button
            type="button"
            aria-label="Send"
            onClick={handleSubmitClick}
            onMouseDown={(e) => e.stopPropagation()}
            className={`send-button nodrag ${creditCost !== undefined ? 'with-credits' : ''}`}
            disabled={disabled || (!value && !hasConnectedTextInput)}
            title={
              creditCost === undefined
                ? undefined
                : usesOwnKey
                ? 'Runs on your own API key — no credits used'
                : `Cost: ${creditCost} credits`
            }
          >
            {creditCost !== undefined && (
              usesOwnKey ? (
                <span className="credit-cost credit-cost-byok">
                  <span role="img" aria-label="your key">🔑</span>
                  <span className="credit-cost-strike">{creditCost}</span>
                </span>
              ) : (
                <span className="credit-cost"><CreditsIcon size={12} />{creditCost}</span>
              )
            )}
            <span className="send-icon"><SendIcon fontSize="medium" /></span>
          </button>
        )}
      </div>
      
      {/* Text Editor Modal - replaces inline editing */}
      <TextEditorModal
        isOpen={isTextEditorOpen}
        value={value}
        onChange={onChange}
        onAccept={handleModalAccept}
        onCancel={handleModalCancel}
        originRect={originRect}
        promptImprovementContext={promptImprovementContext}
        onCondenseRequest={onCondenseRequest}
        placeholder={placeholder}
        title={nodeTitle}
        variant={variant}
        mediaOutputType={mediaOutputType}
        canGenerate={canGenerate}
        onAcceptAndGenerate={onAcceptAndGenerate}
        onSubmit={onSubmit}
        onOpenIris={() => irisService.open()}
        settingsFields={settingsFields}
        settingsValues={settingsValues}
        onSettingsChange={onSettingsChange}
        creditCost={creditCost}
        usesOwnKey={usesOwnKey}
      />
      
      {/* Prompt Improvement Modal (kept for compatibility) */}
      <PromptImprovementModal
        isOpen={isModalOpen}
        originalPrompt={value}
        improvedPrompt={improvedPrompt}
        isLoading={isImproving}
        error={improvementError}
        onUse={handleUseImprovedPrompt}
        onCancel={handleCancelModal}
      />
    </div>
  );
};

/**
 * Memoized MarkdownTextarea to prevent re-renders when parent components update
 * This is critical for input lag prevention
 */
export const MarkdownTextarea = memo(MarkdownTextareaComponent);

export default MarkdownTextarea;
